import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import {
  TEST_VARIABLE_CONTEXT_LIST,
  TEST_VARIABLE_LIFETIME_LIST,
  TEST_VARIABLE_TYPE_LIST,
  type TestVariable,
  type TestVariableContext,
  type TestVariableLifetime,
  type TestVariableType,
} from "../types";
import { clickableProps } from "../utils/keyboard";
import { humanizeText } from "../utils/text";

type Props = {
  variables: TestVariable[];
  onVariablesChange?: (variables: TestVariable[]) => void;
  isLoading?: boolean;
  hideTitle?: boolean;
};

export const EditableVariableList = ({
  variables,
  onVariablesChange,
  isLoading,
  hideTitle = false,
}: Props) => {
  const [editingVariable, setEditingVariable] = useState<TestVariable | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isNewVariable, setIsNewVariable] = useState(false);
  const [expandedVariables, setExpandedVariables] = useState<Set<string>>(new Set());

  const handleEdit = (variable: TestVariable) => {
    setEditingVariable({ ...variable });
    setIsNewVariable(false);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingVariable({
      key: "",
      value: "",
      type: "custom",
      lifetime: "test",
      context: "test",
      is_sensitive: false,
    });
    setIsNewVariable(true);
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!editingVariable) return;

    let updatedVariables: TestVariable[];

    if (isNewVariable) {
      updatedVariables = [...variables, editingVariable];
    } else {
      updatedVariables = variables.map((v) =>
        v.key === editingVariable.key ? editingVariable : v,
      );
    }

    onVariablesChange?.(updatedVariables);
    setDialogOpen(false);
    setEditingVariable(null);
  };

  const handleDelete = (variableKey: string) => {
    const updatedVariables = variables.filter((v) => v.key !== variableKey);
    onVariablesChange?.(updatedVariables);
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setEditingVariable(null);
  };

  const toggleExpanded = (variableKey: string) => {
    setExpandedVariables((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(variableKey)) {
        newSet.delete(variableKey);
      } else {
        newSet.add(variableKey);
      }
      return newSet;
    });
  };

  const getDisplayValue = (variable: TestVariable) => {
    const value = variable.is_sensitive
      ? Array(variable.value.toString().length).fill("*").join("")
      : variable.value.toString();

    if (!value) return "<empty>";

    const lines = value.split("\n");
    const isExpanded = expandedVariables.has(variable.key);

    // If 10 or fewer lines, show everything
    if (lines.length <= 10) {
      return value;
    }

    // If collapsed, show first 5 and last 5 lines
    if (!isExpanded) {
      const hiddenLineCount = lines.length - 10;
      const firstLines = lines.slice(0, 5).join("\n");
      const lastLines = lines.slice(-5).join("\n");
      return `${firstLines}\n[ ... ${hiddenLineCount} lines more ... ]\n${lastLines}`;
    }

    return value;
  };

  const canSave = editingVariable && editingVariable.key.trim() !== "";

  return (
    <Stack direction="column" spacing={2}>
      <Stack
        direction="row"
        justifyContent={hideTitle ? "flex-end" : "space-between"}
        alignItems="center"
      >
        {!hideTitle && <Typography variant="h6">Test Variables</Typography>}
        {onVariablesChange && (
          <Button
            size="small"
            color="primary"
            startIcon={<AddIcon />}
            variant="text"
            onClick={handleAdd}
            disabled={isLoading}
          >
            Add Variable
          </Button>
        )}
      </Stack>

      {variables.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No variables available.
        </Typography>
      ) : (
        <Stack direction="column" spacing={1}>
          {variables.map((variable) => (
            <Stack
              key={variable.key}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{
                p: 2,
                border: "1px solid",
                borderRadius: 1,
                borderColor: "divider",
              }}
            >
              <Stack direction="column" spacing={1} alignItems="stretch" width="100%">
                <Chip
                  variant="outlined"
                  size="small"
                  color="primary"
                  label={<code>{variable.key}</code>}
                  sx={{ alignSelf: "flex-start" }}
                />
                <Card
                  sx={{
                    p: 1,
                    cursor:
                      variable.value.toString().split("\n").length > 10 ? "pointer" : "default",
                    "&:hover":
                      variable.value.toString().split("\n").length > 10
                        ? { bgcolor: "action.hover" }
                        : {},
                  }}
                  {...clickableProps(
                    () =>
                      variable.value.toString().split("\n").length > 10 &&
                      toggleExpanded(variable.key),
                  )}
                  onClick={() =>
                    variable.value.toString().split("\n").length > 10 &&
                    toggleExpanded(variable.key)
                  }
                >
                  <Tooltip
                    title={
                      variable.is_sensitive
                        ? variable.value.toString()
                        : variable.value.toString().split("\n").length > 10
                          ? expandedVariables.has(variable.key)
                            ? "Click to collapse"
                            : "Click to expand"
                          : ""
                    }
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                    >
                      {getDisplayValue(variable)}
                    </Typography>
                  </Tooltip>
                </Card>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="The variable types define how the variable is generates / rotated. For each type the value will be randomly generated or set to a fixed value (custom and sensitive), based on the type.">
                  <Chip
                    variant="outlined"
                    size="small"
                    color="default"
                    label={humanizeText(variable.type)}
                  />
                </Tooltip>
                <Tooltip title="Variable context, i.e. at which level this variable is defined / rotated. E.g. for the 'test' context, it means that the variable will be rotated on each test run.">
                  <Chip
                    variant="filled"
                    size="small"
                    color="default"
                    label={humanizeText(variable.context)}
                  />
                </Tooltip>
                {variable.is_sensitive && (
                  <Tooltip title="This variable is sensitive and its value is hidden in the UI, as it may contain sensitive information.">
                    <Chip variant="filled" size="small" color="secondary" label="Sensitive" />
                  </Tooltip>
                )}
                {onVariablesChange && (
                  <Tooltip title="Edit variable">
                    <IconButton
                      aria-label="Edit variable"
                      size="small"
                      onClick={() => handleEdit(variable)}
                      disabled={isLoading}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {onVariablesChange && (
                  <Tooltip title="Delete variable">
                    <IconButton
                      aria-label="Delete variable"
                      size="small"
                      color="error"
                      onClick={() => handleDelete(variable.key)}
                      disabled={isLoading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{isNewVariable ? "Add Variable" : "Edit Variable"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Key"
              value={editingVariable?.key || ""}
              onChange={(e) =>
                setEditingVariable((prev) => (prev ? { ...prev, key: e.target.value } : null))
              }
              fullWidth
              disabled={!isNewVariable} // Don't allow editing key for existing variables
              required
            />
            <TextField
              label="Value"
              value={editingVariable?.value || ""}
              onChange={(e) =>
                setEditingVariable((prev) => (prev ? { ...prev, value: e.target.value } : null))
              }
              fullWidth
              multiline
              rows={3}
              required
            />
            <TextField
              label="Type"
              select
              value={editingVariable?.type || "custom"}
              onChange={(e) =>
                setEditingVariable((prev) =>
                  prev ? { ...prev, type: e.target.value as TestVariableType } : null,
                )
              }
              fullWidth
            >
              {TEST_VARIABLE_TYPE_LIST.filter((s) => !["sensitive"].includes(s)).map((type) => (
                <MenuItem key={type} value={type}>
                  {humanizeText(type)}
                </MenuItem>
              ))}
              <MenuItem value="sensitive" disabled>
                Sensitive (should be defined in the application config)
              </MenuItem>
            </TextField>
            <TextField
              label="Lifetime"
              select
              value={editingVariable?.lifetime || "test"}
              onChange={(e) =>
                setEditingVariable((prev) =>
                  prev
                    ? {
                        ...prev,
                        lifetime: e.target.value as TestVariableLifetime,
                      }
                    : null,
                )
              }
              fullWidth
            >
              {TEST_VARIABLE_LIFETIME_LIST.map((lifetime) => (
                <MenuItem key={lifetime} value={lifetime}>
                  {humanizeText(lifetime)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Context"
              select
              value={editingVariable?.context || "test"}
              onChange={(e) =>
                setEditingVariable((prev) =>
                  prev
                    ? {
                        ...prev,
                        context: e.target.value as TestVariableContext,
                      }
                    : null,
                )
              }
              fullWidth
            >
              {TEST_VARIABLE_CONTEXT_LIST.map((context) => (
                <MenuItem key={context} value={context}>
                  {humanizeText(context)}
                </MenuItem>
              ))}
            </TextField>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={editingVariable?.is_sensitive || false}
                    onChange={(e) =>
                      setEditingVariable((prev) =>
                        prev ? { ...prev, is_sensitive: e.target.checked } : null,
                      )
                    }
                  />
                }
                label="Sensitive (hide value)"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!canSave || isLoading || ["sensitive"].includes(editingVariable?.type || "")}
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
